import {createContext, ReactNode, useCallback, useContext, useState} from 'react';
import {UserId} from '@legit-health/ui';
import {useNavigate} from '@tanstack/react-router';
import {IMAGE_PROTOCOL_DETAIL} from '@/practitioner/routing/paths';
import PractitionerImageProtocolCaptureContent from '@/practitioner/views/imageProtocol/ImagesProtocolRequest/components/PractitionerImageProtocolCaptureContent';
import useModal from '@/shared/hooks/useModal';
import ImageProtocolCaptureModal, {
  ImageProtocolCaptureResult
} from '@/shared/views/imageProtocol/ImageProtocolCaptureModal';

type OpenValues = {patientId: UserId};

type ContextType = (values: OpenValues) => void;

const Context = createContext<ContextType>(() => {});

export function ImageProtocolCaptureModalContextProvider({children}: {children: ReactNode}) {
  const [patientId, setPatientId] = useState<UserId | null>(null);
  const modal = useModal(false, () => setPatientId(null));
  const navigate = useNavigate();

  const open = useCallback<ContextType>(
    function (values) {
      setPatientId(values.patientId);
      modal.open();
    },
    [modal]
  );

  const handleComplete = useCallback(
    function (result: ImageProtocolCaptureResult) {
      if (!patientId) {
        return;
      }
      modal.close();
      if ('imageProtocolId' in result) {
        navigate({
          to: IMAGE_PROTOCOL_DETAIL,
          params: {patientId, imageProtocolId: result.imageProtocolId}
        });
      }
    },
    [modal, navigate, patientId]
  );

  return (
    <Context.Provider value={open}>
      <ImageProtocolCaptureModal modal={modal}>
        {patientId ? (
          <PractitionerImageProtocolCaptureContent
            patientId={patientId}
            onComplete={handleComplete}
          />
        ) : null}
      </ImageProtocolCaptureModal>
      {children}
    </Context.Provider>
  );
}

export function useImageProtocolCaptureModalContext() {
  return useContext(Context);
}
